<template>
  <WModal :show="show" :title="isEdit ? 'แก้ไขสวัสดิการ' : 'เพิ่มสวัสดิการ'" :subtitle="isEdit ? `รหัส ${form.welfare_id}` : 'กรอกข้อมูลสวัสดิการที่ต้องการให้ระบบแนะนำ'" size="lg" @close="$emit('close')">
    <form id="catalog-form" class="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-3" @submit.prevent="save">
      <div class="md:col-span-2">
        <label class="form-label">ชื่อสวัสดิการ <span class="text-theme-24">*</span></label>
        <input v-model.trim="form.name" type="text" class="form-control" placeholder="เช่น เบี้ยยังชีพผู้สูงอายุ" required>
        <div v-if="errors.name" class="field-error">{{ errors.name }}</div>
      </div>
      <div class="md:col-span-2">
        <label class="form-label">สวัสดิการนี้ใช้กับปีไหน <span class="text-xs text-theme-37">(เลือกเป็นปี หรือกำหนดวันที่เริ่ม–สิ้นสุดเอง)</span></label>
        <PeriodField v-model="form.period" />
      </div>
      <div>
        <label class="form-label">หน่วยงานที่รับผิดชอบ</label>
        <input v-model.trim="form.agency" type="text" class="form-control" placeholder="เช่น กรมกิจการผู้สูงอายุ">
      </div>
      <div>
        <label class="form-label">กระทรวง</label>
        <input v-model.trim="form.ministry" type="text" class="form-control" placeholder="เช่น กระทรวงการพัฒนาสังคมและความมั่นคงของมนุษย์">
      </div>
      <div>
        <label class="form-label">ระดับผู้รับสวัสดิการ</label>
        <select v-model="form.level" class="form-select">
          <option v-for="(l, k) in CATALOG_LEVEL_LABEL" :key="k" :value="k">{{ l }}</option>
        </select>
      </div>
      <div>
        <label class="form-label">มิติที่เกี่ยวข้อง</label>
        <div class="flex flex-wrap gap-1.5 pt-1">
          <button v-for="d in DIMENSIONS" :key="d" type="button" class="chip" :style="dimStyle(d)" @click="toggleDim(d)">
            <CheckIcon v-if="form.dimensions.includes(d)" class="w-3 h-3" /> {{ d }}
          </button>
        </div>
      </div>

      <div class="md:col-span-2 border-t border-gray-200 pt-3 text-sm font-medium text-gray-700">สิทธิประโยชน์</div>
      <div>
        <label class="form-label">ประเภท</label>
        <select v-model="form.benefit.type" class="form-select">
          <option :value="null">ไม่ระบุ</option>
          <option v-for="(l, k) in BENEFIT_TYPE_LABEL" :key="k" :value="k">{{ l }}</option>
        </select>
      </div>
      <div class="grid grid-cols-2 gap-2">
        <div>
          <label class="form-label">จำนวนเงิน (บาท)</label>
          <input v-model="form.benefit.amount_baht" type="number" min="0" step="1" class="form-control" placeholder="เช่น 600">
        </div>
        <div>
          <label class="form-label">ความถี่</label>
          <input v-model.trim="form.benefit.frequency" type="text" class="form-control" placeholder="เช่น เดือน / ครั้งเดียว">
        </div>
      </div>
      <div class="md:col-span-2">
        <label class="form-label">รายละเอียดสิทธิประโยชน์</label>
        <textarea v-model.trim="form.benefit.description" rows="2" class="form-control" placeholder="สรุปสั้น ๆ ว่าครัวเรือนจะได้รับอะไร"></textarea>
      </div>

      <div class="md:col-span-2 border-t border-gray-200 pt-3 text-sm font-medium text-gray-700">เงื่อนไขการรับสิทธิ์</div>
      <div>
        <label class="form-label">กลุ่มเป้าหมาย <span class="text-xs text-theme-37">(คั่นด้วยเครื่องหมายจุลภาค)</span></label>
        <input v-model="targetGroupsText" type="text" class="form-control" placeholder="เช่น ผู้สูงอายุ, ผู้มีรายได้น้อย">
        <div class="flex flex-wrap gap-1 mt-1.5">
          <button v-for="g in TARGET_GROUPS" :key="g" type="button" class="chip" :style="groupStyle(g)" @click="toggleGroup(g)">
            <CheckIcon v-if="hasGroup(g)" class="w-3 h-3" /> {{ g }}
          </button>
        </div>
      </div>
      <div class="grid grid-cols-3 gap-2">
        <div>
          <label class="form-label">อายุต่ำสุด</label>
          <input v-model="form.eligibility.age_min" type="number" min="0" class="form-control">
        </div>
        <div>
          <label class="form-label">อายุสูงสุด</label>
          <input v-model="form.eligibility.age_max" type="number" min="0" class="form-control">
        </div>
        <div>
          <label class="form-label">เพศ</label>
          <select v-model="form.eligibility.gender" class="form-select">
            <option :value="null">ทุกเพศ</option><option value="ชาย">ชาย</option><option value="หญิง">หญิง</option>
          </select>
        </div>
      </div>
      <div class="grid grid-cols-2 gap-2">
        <div>
          <label class="form-label">รายได้ไม่เกิน (บาท)</label>
          <input v-model="form.eligibility.income_max_baht" type="number" min="0" class="form-control">
        </div>
        <div>
          <label class="form-label">หน่วยรายได้</label>
          <select v-model="form.eligibility.income_unit" class="form-select">
            <option :value="null">ไม่ระบุ</option><option value="month">ต่อเดือน</option><option value="year">ต่อปี</option>
          </select>
        </div>
      </div>
      <div>
        <label class="form-label">ลักษณะกลุ่มประชากร <span class="text-xs text-theme-37">(บรรทัดละ 1 ข้อ)</span></label>
        <textarea v-model="traitsText" rows="3" class="form-control" placeholder="เช่น ผู้พิการที่มีบัตรประจำตัวคนพิการ"></textarea>
      </div>
      <div class="md:col-span-2">
        <label class="form-label">เงื่อนไขอื่น ๆ <span class="text-xs text-theme-37">(บรรทัดละ 1 ข้อ)</span></label>
        <textarea v-model="otherText" rows="3" class="form-control" placeholder="เช่น ไม่ได้รับสวัสดิการซ้ำซ้อนจากหน่วยงานรัฐอื่น"></textarea>
      </div>
      <div class="md:col-span-2">
        <label class="form-label">วิธีการยื่นขอ</label>
        <textarea v-model.trim="form.application" rows="2" class="form-control" placeholder="ยื่นที่ไหน ใช้เอกสารอะไร"></textarea>
      </div>
      <div class="md:col-span-2">
        <label class="form-label">เว็บไซต์หน่วยงาน (URL)</label>
        <input v-model.trim="form.url" type="url" class="form-control" placeholder="https://">
        <div v-if="errors.url" class="field-error">{{ errors.url }}</div>
      </div>
      <div v-if="!isEdit">
        <label class="form-label">รหัสสวัสดิการ <span class="text-xs text-theme-37">(เว้นว่างให้ระบบกำหนด)</span></label>
        <input v-model.trim="form.welfare_id" type="text" class="form-control" placeholder="เช่น 103">
      </div>
      <div class="flex items-center gap-3 pt-5">
        <input id="catalog-active" v-model="form.active" type="checkbox" class="form-check-switch">
        <label for="catalog-active" class="text-sm text-gray-700">เปิดใช้งาน (ให้ระบบแนะนำสวัสดิการนี้)</label>
      </div>
    </form>

    <template #footer>
      <div v-if="saveError" class="text-sm text-theme-24 mr-auto whitespace-pre-line text-left">{{ saveError }}</div>
      <button type="button" class="btn btn-outline-secondary" :disabled="saving" @click="$emit('close')">ยกเลิก</button>
      <button type="submit" form="catalog-form" class="btn btn-primary bg-primary-btn border-primary-btn" :disabled="saving">
        <LoadingIcon v-if="saving" icon="oval" class="w-4 h-4 mr-1" /> บันทึก
      </button>
    </template>
  </WModal>
</template>

<script setup>
import { ref, reactive, computed, watch } from "vue";
import WModal from "../components/WModal.vue";
import PeriodField from "../components/PeriodField.vue";
import { welfareApi, apiErrorMessage } from "../api";
import { blankPeriod, yearPeriod, periodError, periodPayload } from "../period";
import { DIMENSIONS, DIMENSION_STYLE, CATALOG_LEVEL_LABEL, BENEFIT_TYPE_LABEL, TARGET_GROUPS } from "../constants";
import { toastSuccess } from "../components/useToast";

const props = defineProps({
  show: { type: Boolean, default: false },
  item: { type: Object, default: null },
});
const emit = defineEmits(["close", "saved"]);

const blank = () => ({
  welfare_id: "", name: "", agency: "", ministry: "", level: "unknown", dimensions: [], active: true,
  period: yearPeriod(),   // new welfare defaults to the current year; existing items without a period stay "every year"
  benefit: { type: null, description: "", amount_baht: "", frequency: "" },
  target_groups: [],
  eligibility: { age_min: "", age_max: "", gender: null, education: null, income_max_baht: "", income_unit: null, population_traits: [], other_conditions: [] },
  application: "", url: "", priority: null, group_classification: null,
});
const form = reactive(blank());
const targetGroupsText = ref("");
const traitsText = ref("");
const otherText = ref("");
const errors = reactive({});
const saveError = ref("");
const saving = ref(false);
const isEdit = computed(() => !!(props.item && props.item.welfare_id));

watch(() => props.show, (v) => { if (v) reset(); });

function reset() {
  Object.assign(form, blank());
  Object.keys(errors).forEach((k) => delete errors[k]);
  saveError.value = "";
  if (props.item) {
    const it = props.item;
    form.welfare_id = it.welfare_id;
    form.name = it.name || "";
    form.agency = it.agency || "";
    form.ministry = it.ministry || "";
    form.level = it.level || "unknown";
    form.dimensions = [...(it.dimensions || [])];
    form.active = it.active !== false;
    form.period = { ...blankPeriod(), ...(it.period || {}) };
    form.benefit = { type: null, description: "", amount_baht: "", frequency: "", ...(it.benefit || {}) };
    form.benefit.amount_baht = form.benefit.amount_baht == null ? "" : form.benefit.amount_baht;
    form.benefit.description = form.benefit.description || "";
    form.benefit.frequency = form.benefit.frequency || "";
    form.target_groups = [...(it.target_groups || [])];
    form.eligibility = { ...blank().eligibility, ...(it.eligibility || {}) };
    for (const k of ["age_min", "age_max", "income_max_baht"]) if (form.eligibility[k] == null) form.eligibility[k] = "";
    form.application = it.application || "";
    form.url = it.url || "";
    form.priority = it.priority || null;
    form.group_classification = it.group_classification || null;
  }
  targetGroupsText.value = form.target_groups.join(", ");
  traitsText.value = (form.eligibility.population_traits || []).join("\n");
  otherText.value = (form.eligibility.other_conditions || []).join("\n");
}

const dimStyle = (d) => form.dimensions.includes(d)
  ? { ...DIMENSION_STYLE[d], border: `1px solid ${DIMENSION_STYLE[d].color}`, cursor: "pointer" }
  : { color: "#728096", background: "#f7f9fb", border: "1px solid #e2e8f0", cursor: "pointer" };
const toggleDim = (d) => {
  const i = form.dimensions.indexOf(d);
  if (i >= 0) form.dimensions.splice(i, 1); else form.dimensions.push(d);
};

const splitList = (text, sep) => String(text || "").split(sep).map((s) => s.trim()).filter(Boolean);
const hasGroup = (g) => splitList(targetGroupsText.value, ",").includes(g);
const toggleGroup = (g) => {
  const list = splitList(targetGroupsText.value, ",");
  targetGroupsText.value = (hasGroup(g) ? list.filter((x) => x !== g) : [...list, g]).join(", ");
};
const groupStyle = (g) => hasGroup(g)
  ? { color: "#415A77", background: "#eef1f6", border: "1px solid #415A77", cursor: "pointer" }
  : { color: "#728096", background: "#f7f9fb", border: "1px solid #e2e8f0", cursor: "pointer" };
const numOrNull = (v) => (v === "" || v === null || v === undefined ? null : Number(v));

function validate() {
  Object.keys(errors).forEach((k) => delete errors[k]);
  if (!form.name) errors.name = "ต้องระบุชื่อสวัสดิการ";
  if (form.url && !/^https?:\/\//i.test(form.url)) errors.url = "URL ต้องขึ้นต้นด้วย http:// หรือ https://";
  if (periodError(form.period)) errors.period = periodError(form.period);   // shown inside PeriodField
  return Object.keys(errors).length === 0;
}

async function save() {
  if (!validate()) return;
  saving.value = true;
  saveError.value = "";
  const body = {
    name: form.name, agency: form.agency || null, ministry: form.ministry || null, level: form.level,
    dimensions: form.dimensions, active: form.active, period: periodPayload(form.period),
    benefit: { type: form.benefit.type || null, description: form.benefit.description || null, amount_baht: numOrNull(form.benefit.amount_baht), frequency: form.benefit.frequency || null },
    target_groups: splitList(targetGroupsText.value, ","),
    eligibility: {
      ...form.eligibility,
      age_min: numOrNull(form.eligibility.age_min), age_max: numOrNull(form.eligibility.age_max),
      income_max_baht: numOrNull(form.eligibility.income_max_baht),
      population_traits: splitList(traitsText.value, "\n"), other_conditions: splitList(otherText.value, "\n"),
    },
    application: form.application || null, url: form.url || null, priority: form.priority, group_classification: form.group_classification,
  };
  try {
    const res = isEdit.value
      ? await welfareApi.catalog.update(props.item.welfare_id, body)
      : await welfareApi.catalog.create(form.welfare_id ? { ...body, welfare_id: form.welfare_id } : body);
    toastSuccess(isEdit.value ? "บันทึกการแก้ไขแล้ว" : "เพิ่มสวัสดิการแล้ว");
    emit("saved", res && res.item);
  } catch (e) {
    saveError.value = apiErrorMessage(e);
  } finally {
    saving.value = false;
  }
}
</script>
